/**
 * Each character's 结构 and 部首, as the character worksheet prints them.
 *
 * `partials/tools/worksheet.html` writes them into the page from
 * `hugo.Data.hanzi`, the record the 词典's 汉字 breakdown draws, so a sheet
 * and an entry give the same answers. A character is one string, a mark for
 * its structure followed by its 部首 as written in it. The mark is the
 * structure's Ideographic Description Character (⿰ for 左右结构), `-` for a
 * 独体字, and nothing where the record gives no structure.
 */

/** A structure, as the 结构 pages name it. */
export interface Structure {
  /** Its Ideographic Description Character, or empty for a 独体字. */
  readonly symbol: string;
  readonly name: string;
  readonly english: string;
}

/** What a sheet says of one character's make-up. */
export interface CharacterFacts {
  /** Its structure, where the record gives one. */
  readonly structure: Structure | undefined;

  /** Its 部首, in the form it takes in the character. */
  readonly radical: string;
}

/** The mark a 独体字 is written with, having no description character. */
const independent = "-";

/** The facts of every character the site has a record of. */
export class HanziFacts {
  readonly #structures: ReadonlyMap<string, Structure>;
  readonly #characters: ReadonlyMap<string, string>;

  constructor(properties: {
    readonly structures: ReadonlyMap<string, Structure>;
    readonly characters: ReadonlyMap<string, string>;
  }) {
    this.#structures = properties.structures;
    this.#characters = properties.characters;
  }

  /** Nothing known of any character, for a page that carries no facts. */
  static none(): HanziFacts {
    return new HanziFacts({ structures: new Map(), characters: new Map() });
  }

  /**
   * Read the facts the page carries.
   *
   * Read defensively, since a malformed object should cost the sheet its
   * structures and nothing else.
   * @param text - The JSON the template wrote.
   */
  static parse(text: string): HanziFacts {
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      return HanziFacts.none();
    }
    if (typeof body !== "object" || body === null) {
      return HanziFacts.none();
    }
    const { structures, characters } = body as Record<string, unknown>;
    const named = new Map<string, Structure>();
    for (const [mark, structure] of entries(structures)) {
      const { name, english } = (structure ?? {}) as Record<string, unknown>;
      if (typeof name === "string" && typeof english === "string") {
        named.set(mark, {
          symbol: mark === independent ? "" : mark,
          name,
          english,
        });
      }
    }
    const written = new Map<string, string>();
    for (const [character, facts] of entries(characters)) {
      if (typeof facts === "string") {
        written.set(character, facts);
      }
    }

    return new HanziFacts({ structures: named, characters: written });
  }

  /** What the record says of a character, or nothing for one it lacks. */
  of(character: string): CharacterFacts | undefined {
    const facts = this.#characters.get(character);
    if (facts === undefined) {
      return undefined;
    }
    const [first = "", ...rest] = Array.from(facts);
    const structure = this.#structures.get(first);

    return structure === undefined
      ? { structure: undefined, radical: facts }
      : { structure, radical: rest.join("") };
  }
}

function entries(value: unknown): readonly [string, unknown][] {
  return typeof value === "object" && value !== null
    ? Object.entries(value)
    : [];
}
